import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Services = () => {
  const servicesData = {
    ecommerce: {
      title: 'Ecommerce Marketplace Services',
      description: 'Comprehensive marketplace management and growth solutions for leading e-commerce platforms.',
      platforms: {
        amazon: {
          name: 'Amazon',
          color: 'from-orange-500 to-yellow-500',
          services: [
            {
              id: 'amazon-account',
              title: 'Amazon Account Management Services',
              description: 'Complete end-to-end management of your Amazon seller account including inventory, pricing, customer service, and compliance.',
              link: '/amazon-account-management-services',
            },
            {
              id: 'amazon-listing',
              title: 'Amazon Product Listing & SEO Optimization Services',
              description: 'Create high-converting product listings optimized for Amazon A10 algorithm with keyword research and SEO.',
              link: '/amazon-listing-seo-optimization',
            },
            {
              id: 'amazon-ppc',
              title: 'Amazon PPC Management & Advertising Services',
              description: 'Data-driven Amazon PPC campaigns that maximize ROI and minimize ACoS with continuous optimization.',
              link: '/amazon-ppc-advertising-services',
            },
            {
              id: 'amazon-aplus',
              title: 'Amazon A+ Content Design & Infographic Services',
              description: 'Premium A+ content creation and infographic images to enhance product storytelling and boost conversions.',
              link: '/amazon-aplus-content-design',
            },
            {
              id: 'amazon-brand-store',
              title: 'Amazon Brand Store Setup & Optimization',
              description: 'Design and develop engaging Amazon Brand Stores with multi-page layouts and analytics integration.',
              link: '/amazon-brand-store-setup',
            },
          ]
        },
        flipkart: {
          name: 'Flipkart',
          color: 'from-blue-600 to-blue-400',
          services: [
            {
              id: 'flipkart-account',
              title: 'Flipkart Account Management Services',
              description: 'Strategic management of your Flipkart seller account to maximize visibility and sales performance.',
              link: '/flipkart-account-management-services',
            },
            {
              id: 'flipkart-listing',
              title: 'Flipkart Product Listing & SEO Optimization',
              description: 'Optimize product listings for Flipkart search algorithm with compelling content and SEO best practices.',
              link: '/flipkart-listing-seo-optimization',
            },
            {
              id: 'flipkart-ads',
              title: 'Flipkart Advertising Management Services',
              description: 'Manage Flipkart advertising campaigns to drive qualified traffic and improve conversion rates.',
              link: '/flipkart-advertising-management',
            },
          ]
        },
        myntra: {
          name: 'Myntra',
          color: 'from-pink-500 to-rose-500',
          services: [
            {
              id: 'myntra-listing',
              title: 'Myntra Product Listing & Catalog Optimization Services',
              description: 'Professional product listing and catalog management optimized for fashion and lifestyle categories.',
              link: '/myntra-listing-catalog-optimization',
            },
            {
              id: 'myntra-account',
              title: 'Myntra Account Management Services',
              description: 'Complete account management including inventory, pricing strategy, and promotional campaigns.',
              link: '/myntra-account-management-services',
            },
          ]
        },
        ajio: {
          name: 'Ajio',
          color: 'from-indigo-500 to-purple-500',
          services: [
            {
              id: 'ajio-listing',
              title: 'Ajio Product Listing & Catalog Management',
              description: 'Create optimized product listings with compelling content for Ajio fashion marketplace.',
              link: '/ajio-account-management-services',
            },
            {
              id: 'ajio-account',
              title: 'Ajio Account Management Services',
              description: 'End-to-end account management including compliance, inventory, and customer experience.',
              link: '/ajio-account-management-services',
            },
          ]
        }
      }
    },
    quickCommerce: {
      title: 'Quick Commerce Services',
      description: 'Rapid growth solutions for quick commerce and food delivery platforms.',
      platforms: {
        blinkit: {
          name: 'Blinkit',
          color: 'from-yellow-400 to-orange-400',
          services: [
            {
              id: 'blinkit-onboarding',
              title: 'Blinkit Seller Onboarding Services',
              description: 'Complete seller onboarding assistance with documentation, compliance, and account setup.',
              link: '/blinkit-seller-onboarding',
            },
            {
              id: 'blinkit-account',
              title: 'Blinkit Account Management & Optimization',
              description: 'Manage your Blinkit account with inventory optimization and performance tracking.',
              link: '/blinkit-account-management',
            },
          ]
        },
        swiggyInstamart: {
          name: 'Swiggy Instamart',
          color: 'from-orange-500 to-red-500',
          services: [
            {
              id: 'swiggy-instamart-onboarding',
              title: 'Swiggy Instamart Seller Onboarding',
              description: 'Seamless onboarding process for Swiggy Instamart with complete documentation support.',
              link: '/quick-commerce-account-management',
            },
            {
              id: 'swiggy-instamart-account',
              title: 'Swiggy Instamart Account Management Services',
              description: 'Professional account management to maximize sales on Swiggy Instamart platform.',
              link: '/quick-commerce-account-management',
            },
          ]
        },
        zomato: {
          name: 'Zomato',
          color: 'from-red-500 to-pink-500',
          services: [
            {
              id: 'zomato-onboarding',
              title: 'Zomato Seller Onboarding Services',
              description: 'Expert guidance for Zomato seller registration and account activation.',
              link: '/quick-commerce-account-management',
            },
            {
              id: 'zomato-account',
              title: 'Zomato Marketplace Account Management',
              description: 'Comprehensive account management including menu optimization and order fulfillment.',
              link: '/quick-commerce-account-management',
            },
          ]
        },
        swiggy: {
          name: 'Swiggy',
          color: 'from-orange-600 to-orange-400',
          services: [
            {
              id: 'swiggy-onboarding',
              title: 'Swiggy Seller Onboarding Services',
              description: 'Complete support for Swiggy restaurant onboarding with compliance assistance.',
              link: '/quick-commerce-account-management',
            },
            {
              id: 'swiggy-account',
              title: 'Swiggy Account Management & Growth',
              description: 'Strategic account management to grow your business on Swiggy platform.',
              link: '/quick-commerce-account-management',
            },
          ]
        }
      }
    },
    local: {
      title: 'Local Search & Google Business Optimization',
      description: 'We help businesses improve their local visibility and strengthen their digital presence through strategic Google Business and local search optimization.',
      color: 'from-blue-500 to-green-500',
      id: 'local-search'
    }
  };

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>E-commerce & Quick Commerce Services India | Obiquiste Solutions</title>
        <meta name="description" content="Complete marketplace services: Amazon, Flipkart, Myntra, Ajio, Blinkit, Swiggy Instamart, Zomato. Account management, listing, ads & seller onboarding." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/services" />
      </Helmet>
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 data-testid="services-heading" className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Comprehensive marketplace solutions to help your brand grow and scale profitably across all major platforms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ecommerce Marketplace Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="mb-12">
            <h2 className="font-heading text-4xl font-bold mb-4">
              <span className="text-gradient">{servicesData.ecommerce.title}</span>
            </h2>
            <p className="text-lg text-slate-600">{servicesData.ecommerce.description}</p>
          </div>

          {Object.values(servicesData.ecommerce.platforms).map((platform, pIdx) => (
            <div key={pIdx} className="mb-16">
              <div className="mb-8">
                <h3 className={`font-heading text-3xl font-bold mb-2 bg-gradient-to-r ${platform.color} bg-clip-text text-transparent`}>
                  {platform.name}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {platform.services.map((service, sIdx) => (
                  <Link key={service.id} to={service.link}>
                    <motion.div
                      id={service.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: sIdx * 0.1 }}
                      className="group bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer h-full"
                      data-testid={`service-${service.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-heading text-lg font-semibold text-slate-900 group-hover:text-primary transition-colors">
                          {service.title}
                        </h4>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      </div>
                      <p className="text-slate-600 leading-relaxed text-sm">{service.description}</p>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Commerce Services */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="mb-12">
            <h2 className="font-heading text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">
                {servicesData.quickCommerce.title}
              </span>
            </h2>
            <p className="text-lg text-slate-600">{servicesData.quickCommerce.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.values(servicesData.quickCommerce.platforms).map((platform, pIdx) => (
              <div key={pIdx} className="bg-white rounded-2xl p-8 shadow-md">
                <h3 className={`font-heading text-2xl font-bold mb-6 bg-gradient-to-r ${platform.color} bg-clip-text text-transparent`}>
                  {platform.name}
                </h3>
                <div className="space-y-4">
                  {platform.services.map((service, sIdx) => (
                    <Link key={service.id} to={service.link}>
                      <motion.div
                        id={service.id}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: sIdx * 0.1 }}
                        className="group p-4 bg-slate-50 rounded-lg hover:bg-white hover:shadow-md border border-transparent hover:border-primary/20 transition-all cursor-pointer"
                        data-testid={`service-${service.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-heading font-semibold text-slate-900 group-hover:text-primary transition-colors text-sm">
                            {service.title}
                          </h4>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                        <p className="text-slate-600 leading-relaxed text-xs">{service.description}</p>
                      </motion.div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Search & Google Business Optimization */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            id={servicesData.local.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r ${servicesData.local.color} bg-clip-text text-transparent`}>
                {servicesData.local.title}
              </span>
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              {servicesData.local.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-xl mb-8 text-slate-300">
              Every brand is unique. Let's discuss a tailored strategy for your marketplace growth.
            </p>
            <Link to="/contact">
              <Button
                data-testid="services-cta-custom"
                className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full text-base font-semibold"
              >
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
